import type { PopularResearchApi } from 'apiConnector/discover/publication';
import { publicationConnector } from 'apiConnector/discover/publication';
import type { ResearchApi } from 'apiConnector/discover/research';
import { researchConnector } from 'apiConnector/discover/research';
import classNames from 'classnames';
import { useFetchArrayData } from 'hooks/useFetchData';
import { FC, useCallback } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type { AppState } from 'state/state.model';
import { getInsightPortalUrl, getMacroMattersUrl, getThematicInsightsUrl } from 'state/user';
import type { SgBsTheme } from 'state/userPreferences/userPreferences.model';
import { httpClient } from 'utils/http/httpClient';

import { RequestAccess } from '../RequestAccess';
import { ModalVideoContainer } from '../video';
import { StyledGrid, StyledHeader } from './Grid';
import type { MostViewedResearchItem, RecommendedResearchItem } from './model';
import { PopularResearchContainer } from './PopularResearchContainer';
import { ResearchHeader } from './presentation';
import { RecommendedResearchContainer } from './RecommendedResearchContainer';

interface ResearchContainerProps {
  theme: SgBsTheme;

  researchApi: ResearchApi;
  popularResearchApi: PopularResearchApi;
  urls: {
    insightPublicPortalUrl: string;
  };
}

export const ResearchContainer: FC<ResearchContainerProps> = ({
  researchApi: { fetchRecommendedResearch },
  popularResearchApi: { fetchPopularResearch },
  urls,
  theme,
}) => {
  const fetchPublications = useCallback(() => fetchPopularResearch(), [fetchPopularResearch]);
  const fetchRecommended = useCallback(() => fetchRecommendedResearch(), [fetchRecommendedResearch]);

  const [popularStatus, popularItems, popularRetry] = useFetchArrayData<MostViewedResearchItem>(fetchPublications);
  const [recommendedStatus, recommendedItems, recommendedRetry] =
    useFetchArrayData<RecommendedResearchItem>(fetchRecommended);

  const { formatMessage } = useIntl();
  const insightPortalUrl = useSelector((state: AppState) => getInsightPortalUrl(state));
  const thematicInsightsUrl = useSelector((state: AppState) => getThematicInsightsUrl(state));
  const macroMattersUrl = useSelector((state: AppState) => getMacroMattersUrl(state));

  const hasRecommendedItems = recommendedStatus !== 'empty';
  const hasPopularItems = popularStatus !== 'empty';
  const hasTwoColumns = hasPopularItems && hasRecommendedItems;

  const requestAccess = popularStatus === 'unauthorized' || recommendedStatus === 'unauthorized';

  const portalUrl = requestAccess ? urls.insightPublicPortalUrl : insightPortalUrl;

  const InsightRequestAccess: FC<FallbackProps> = () => (
    <RequestAccess e2e="popular-research-request-access" url={insightPortalUrl} labelKey="discover.research.portal" />
  );

  return (
    <ModalVideoContainer>
      <ResearchHeader
        theme={theme}
        externalPortalUrl={portalUrl}
        thematicInsightsUrl={thematicInsightsUrl}
        macroMattersUrl={macroMattersUrl}
      />
      <div className="container px-0">
        <ErrorBoundary FallbackComponent={InsightRequestAccess}>
          <StyledGrid className={classNames('pb-2', hasTwoColumns ? 'two-columns' : 'single-column')}>
            {hasRecommendedItems && (
              <>
                <StyledHeader className="border-bottom pb-3 grid-recommended-header">
                  {formatMessage({ id: 'discover.research.recommended' })}
                </StyledHeader>
                <RecommendedResearchContainer
                  status={recommendedStatus}
                  items={recommendedItems}
                  retry={recommendedRetry}
                  className="border-bottom pb-3 grid-recommended-item"
                />
              </>
            )}
            {hasPopularItems && (
              <>
                <StyledHeader className="border-bottom pb-3 grid-popular-header">
                  {formatMessage({ id: 'discover.research.popular' })}
                </StyledHeader>
                <PopularResearchContainer
                  status={popularStatus}
                  items={popularItems}
                  retry={popularRetry}
                  className="border-bottom pb-3 grid-popular-item"
                />
              </>
            )}
          </StyledGrid>
        </ErrorBoundary>
      </div>
    </ModalVideoContainer>
  );
};

const researchApi = researchConnector(httpClient);
const popularResearchApi = publicationConnector(httpClient);
export const ResearchWithApi: FC<{ theme: SgBsTheme }> = ({ theme }) => (
  <ResearchContainer
    theme={theme}
    researchApi={researchApi}
    popularResearchApi={popularResearchApi}
    urls={window.sgmeConfiguration}
  />
);
